import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import AppHeader from "../../../components/AppHeader/AppHeader";
import SpinnerComponent from "../../../components/SpinnerComponent/SpinnerComponent";
import LastLiveReplaySection from "./components/LastLiveReplaySection";
import LiveReplayListSection from "./components/LiveReplayListSection";
import { liveService } from "../../../services";
import "./index.less";

interface RebroadcastItem {
  id: string;
  thumbnail: string;
  liveDescription: string;
  date: string;
  time: string;
  creatorName?: string;
  tags?: string[];
}

interface ApiRebroadcastItem {
  _id: string;
  creator: {
    _id?: string;
    avatarPath?: string;
    avatar?: string;
    name?: string;
    username?: string;
    contentType?: string[];
  };
  scheduleData?: {
    message?: string;
    media?: {
      absolutePath?: string;
    };
  };
  createdAt?: string;
  lastStreamingTime?: string;
  isSubscribed?: boolean;
}

interface ApiRebroadcastsResponse {
  week: ApiRebroadcastItem[];
  month: ApiRebroadcastItem[];
}

interface RebroadcastsResponse {
  lastLive?: RebroadcastItem;
  weeklyBroadcasts?: RebroadcastItem[];
  last30DaysBroadcasts?: RebroadcastItem[];
}

const LiveReplaysPage = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [rebroadcasts, setRebroadcasts] = useState<RebroadcastsResponse>({
    lastLive: undefined,
    weeklyBroadcasts: [],
    last30DaysBroadcasts: [],
  });
  const hasFetched = useRef(false);

  const transformApiData = (apiData: ApiRebroadcastsResponse): RebroadcastsResponse => {
    const formatDateTime = (dateString?: string) => {
      if (!dateString) return { date: "", time: "" };
      const date = new Date(dateString);
      return {
        date: date.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" }),
        time: date.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }),
      };
    };

    const transformItem = (item: ApiRebroadcastItem): RebroadcastItem => ({
      id: item._id,
      thumbnail: item.scheduleData?.media?.absolutePath || item.creator.avatar || item.creator.avatarPath || "",
      creatorName: item.creator.name || item.creator.username || "",
      tags: item.creator.contentType || [],
      liveDescription: item.scheduleData?.message || "",
      ...formatDateTime(item.lastStreamingTime || item.createdAt),
    });

    const weekItems = apiData.week || [];
    const monthItems = apiData.month || [];

    return {
      lastLive: weekItems.length > 0 ? transformItem(weekItems[0]) : undefined,
      weeklyBroadcasts: weekItems.map(transformItem),
      last30DaysBroadcasts: monthItems.map(transformItem),
    };
  };

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;

    const fetchRebroadcasts = async () => {
      try {
        setLoading(true);
        const apiData = (await liveService.getRebroadcasts()) as ApiRebroadcastsResponse;
        const transformedData = transformApiData(apiData);
        setRebroadcasts(transformedData);
      } catch (err: any) {
        setError(err || "Failed to load rebroadcasts");
      } finally {
        setLoading(false);
      }
    };

    fetchRebroadcasts();
  }, []);

  const { lastLive, weeklyBroadcasts, last30DaysBroadcasts } = rebroadcasts;

  if (loading) {
    return (
      <div className="live-replays-view">
        <AppHeader title={t("live-replays")} fontSize="28" backButtonDestination="/common/setting" />
        <div className="live-replays-content" style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "200px" }}>
          <SpinnerComponent size={40} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="live-replays-view">
        <AppHeader title={t("live-replays")} fontSize="28" backButtonDestination="/common/setting" />
        <div className="live-replays-content">{error}</div>
      </div>
    );
  }

  return (
    <div className="live-replays-view">
      <AppHeader title={t("live-replays")} fontSize="28" backButtonDestination="/common/setting" />
      <div className="live-replays-content">
        {lastLive && (
          <LastLiveReplaySection
            thumbnail={lastLive.thumbnail}
            creatorName={lastLive.creatorName || ""}
            liveDescription={lastLive.liveDescription}
            tags={lastLive.tags || []}
            replayId={lastLive.id}
            showSubscribeButton={false}
          />
        )}
        {weeklyBroadcasts && weeklyBroadcasts.length > 0 && <LiveReplayListSection title={t("weekly-broadcasts")} items={weeklyBroadcasts} />}
        {last30DaysBroadcasts && last30DaysBroadcasts.length > 0 && (
          <LiveReplayListSection title={t("last-30-days-broadcasts")} items={last30DaysBroadcasts} />
        )}
        {!lastLive && (!weeklyBroadcasts || weeklyBroadcasts.length === 0) && (!last30DaysBroadcasts || last30DaysBroadcasts.length === 0) && (
          <div className="no-rebroadcasts">{t("no-rebroadcasts")}</div>
        )}
      </div>
    </div>
  );
};

export default LiveReplaysPage;
